import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { OrderData } from "@/pages/checkout/types";

interface OrderSuccessProps {
  data: OrderData;
}

function getAdminPhone(): string {
  try {
    const saved = localStorage.getItem("whatsapp_settings");
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.adminWhatsApp) return parsed.adminWhatsApp;
    }
    // fallback to store settings
    const store = localStorage.getItem("store_settings");
    if (store) {
      const parsed = JSON.parse(store);
      if (parsed.adminWhatsApp) return parsed.adminWhatsApp;
    }
  } catch { /* ignore */ }
  return "966541138847";
}

function buildWhatsAppMessage(data: OrderData): string {
  const sep = "─────────────────";
  const itemsLines = data.items
    .map((i) => `  • ${i.nameAr} × ${i.qty}  ←  ${(i.price * i.qty).toLocaleString()} ر.س`)
    .join("\n");

  const lines = [
    "🛒 *طلب جديد — متجر الإبداع*",
    sep,
    `🔖 *رقم الطلب:* #${data.orderRef}`,
    sep,
    "👤 *بيانات العميل:*",
    `  • الاسم: ${data.name}`,
    `  • الجوال: ${data.phone}`,
    `  • المدينة: ${data.city}`,
    `  • العنوان: ${data.address}`,
    ...(data.notes ? [`  • ملاحظات: ${data.notes}`] : []),
    sep,
    "📦 *المنتجات المطلوبة:*",
    itemsLines,
    sep,
    `💳 *طريقة الدفع:* ${data.paymentLabel}`,
    ...(data.discountCode ? [
      `🏷️ *كود الخصم:* ${data.discountCode} (${data.discountPercent}% خصم)`,
      `✂️ *قيمة الخصم:* — ${data.discountAmount.toLocaleString()} ر.س`,
    ] : []),
    `💰 *الإجمالي النهائي: ${data.finalPrice.toLocaleString()} ر.س*`,
    "🚚 *الشحن:* مجاني",
    sep,
    "أرجو تأكيد الطلب وتحديد موعد التسليم. شكراً 🙏",
  ];

  return encodeURIComponent(lines.join("\n"));
}

export default function OrderSuccess({ data }: OrderSuccessProps) {
  const circleRef = useRef<SVGCircleElement>(null);
  const [copied, setCopied] = useState(false);
  const [notifVisible, setNotifVisible] = useState(true);

  const adminPhone = getAdminPhone();
  const whatsappUrl = `https://wa.me/${adminPhone}?text=${buildWhatsAppMessage(data)}`;
  const autoSent = data.adminAutoSent ?? false;

  useEffect(() => {
    if (circleRef.current) {
      circleRef.current.style.strokeDashoffset = "0";
    }
    const t = setTimeout(() => setNotifVisible(false), 10000);
    return () => clearTimeout(t);
  }, []);

  const handleCopyRef = () => {
    navigator.clipboard.writeText(`#${data.orderRef}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div dir="rtl" className="flex flex-col items-center justify-center text-center py-12 px-4 min-h-[60vh]">

      {/* Admin notification banner */}
      {notifVisible && (
        <div className="w-full max-w-md mb-6">
          <div className={`flex items-center gap-3 border rounded-2xl px-4 py-3 ${
            autoSent
              ? "bg-green-500/10 border-green-500/25"
              : "bg-amber-400/8 border-amber-400/25"
          }`}>
            <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${
              autoSent ? "bg-green-500/15" : "bg-amber-400/15"
            }`}>
              {autoSent ? (
                <i className="ri-whatsapp-line text-green-400 text-base"></i>
              ) : (
                <i className="ri-whatsapp-line text-amber-400 text-base"></i>
              )}
            </div>
            <div className="flex-1 text-right">
              {autoSent ? (
                <>
                  <p className="text-green-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    تم إرسال إشعار المتجر تلقائياً ✓
                  </p>
                  <p className="text-green-400/60 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    وصل إشعار طلبك #{data.orderRef} لفريق المبيعات عبر واتساب
                  </p>
                </>
              ) : (
                <>
                  <p className="text-amber-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    تم فتح واتساب لإرسال الطلب
                  </p>
                  <p className="text-amber-400/60 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    أرسل الرسالة لفريق المبيعات — أو اضغط &quot;إعادة الإرسال&quot; أدناه
                  </p>
                </>
              )}
            </div>
            <button
              onClick={() => setNotifVisible(false)}
              className={`w-5 h-5 flex items-center justify-center transition-colors cursor-pointer flex-shrink-0 ${
                autoSent ? "text-green-400/40 hover:text-green-400" : "text-amber-400/40 hover:text-amber-400"
              }`}
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          </div>
        </div>
      )}

      {/* Animated checkmark */}
      <div className="relative w-28 h-28 mb-8">
        <svg className="w-full h-full" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" fill="none" stroke="#1a1a1a" strokeWidth="4" />
          <circle
            ref={circleRef}
            cx="50" cy="50" r="45"
            fill="none"
            stroke="#f59e0b"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="283"
            strokeDashoffset="283"
            style={{ transition: "stroke-dashoffset 1s ease", transformOrigin: "center", transform: "rotate(-90deg)" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 flex items-center justify-center bg-amber-400/15 rounded-full">
            <i className="ri-check-line text-3xl text-amber-400"></i>
          </div>
        </div>
      </div>

      <h1 className="text-white font-black text-3xl mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
        تم استلام طلبك!
      </h1>
      <p className="text-gray-400 text-base mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
        شكراً لك يا <strong className="text-amber-400">{data.name}</strong>
      </p>
      <p className="text-gray-500 text-sm mb-8 leading-relaxed max-w-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
        سيتواصل معك فريقنا على <strong className="text-white">{data.phone}</strong> قريباً لتأكيد الطلب وتحديد موعد التوصيل.
      </p>

      {/* Order ref card */}
      <button
        onClick={handleCopyRef}
        className="bg-[#111] border border-amber-400/20 hover:border-amber-400/50 rounded-2xl px-10 py-4 mb-8 text-center cursor-pointer transition-all group"
      >
        <p className="text-gray-500 text-xs mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>رقم الطلب — اضغط للنسخ</p>
        <p className="text-amber-400 font-black text-2xl tracking-widest" style={{ fontFamily: "'Cairo', sans-serif" }}>
          #{data.orderRef}
        </p>
        <p className="text-xs mt-1 transition-colors" style={{ fontFamily: "'Cairo', sans-serif", color: copied ? "#4ade80" : "#4b5563" }}>
          {copied ? "✓ تم النسخ!" : "انقر للنسخ"}
        </p>
      </button>

      {/* Points earned card */}
      {(() => {
        const earnedPoints = Math.floor(data.finalPrice / 10);
        if (earnedPoints <= 0) return null;
        return (
          <div className="w-full max-w-md mb-6">
            <div className="relative overflow-hidden bg-gradient-to-l from-amber-400/12 to-amber-400/5 border border-amber-400/30 rounded-2xl px-5 py-4">
              <div className="absolute -right-4 -top-4 w-20 h-20 bg-amber-400/8 rounded-full blur-xl pointer-events-none" />
              <div className="relative flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-amber-400/15 border border-amber-400/25 rounded-2xl flex-shrink-0">
                  <i className="ri-vip-crown-line text-amber-400 text-2xl"></i>
                </div>
                <div className="flex-1 text-right">
                  <p className="text-gray-400 text-xs mb-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    نقاط مكافأة ستُضاف لحسابك
                  </p>
                  <p className="text-amber-400 font-black text-2xl" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    +{earnedPoints} نقطة
                  </p>
                  <p className="text-amber-400/60 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    تُضاف تلقائياً عند توصيل طلبك · نقطة لكل 10 ر.س
                  </p>
                </div>
              </div>
              {/* Progress hint */}
              <div className="relative mt-3 pt-3 border-t border-amber-400/15 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <i className="ri-medal-line text-amber-700 text-xs"></i>
                  <span className="text-gray-500 text-[11px]" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    اجمع 100 نقطة للوصول للمستوى الفضي
                  </span>
                </div>
                <a
                  href="/rewards"
                  className="text-amber-400 hover:text-amber-300 text-[11px] font-bold transition-colors cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  عرض مكافآتي
                </a>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Track Order Banner */}
      <Link
        to={`/track-order?ref=${data.orderRef}`}
        className="w-full max-w-md mb-6 block group cursor-pointer"
      >
        <div className="relative overflow-hidden bg-gradient-to-l from-amber-400/10 to-amber-400/5 border border-amber-400/30 hover:border-amber-400/60 rounded-2xl px-5 py-4 transition-all">
          {/* Decorative glow */}
          <div className="absolute -left-6 top-1/2 -translate-y-1/2 w-20 h-20 bg-amber-400/10 rounded-full blur-xl pointer-events-none" />
          <div className="relative flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-amber-400/15 rounded-xl flex-shrink-0">
              <i className="ri-map-pin-time-line text-amber-400 text-2xl"></i>
            </div>
            <div className="flex-1 text-right">
              <p className="text-white font-black text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                تتبع طلبك الآن
              </p>
              <p className="text-gray-400 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                اعرف أين وصل طلبك #{data.orderRef} في أي وقت
              </p>
            </div>
            <div className="w-8 h-8 flex items-center justify-center bg-amber-400/10 group-hover:bg-amber-400/20 rounded-full transition-colors flex-shrink-0">
              <i className="ri-arrow-left-s-line text-amber-400 text-lg"></i>
            </div>
          </div>
        </div>
      </Link>

      {/* Order details summary */}
      <div className="w-full max-w-md bg-[#111] border border-white/5 rounded-2xl overflow-hidden mb-8 text-right">
        {/* Customer info */}
        <div className="px-5 py-4 border-b border-white/5">
          <p className="text-gray-500 text-xs mb-3 flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
            <i className="ri-map-pin-line text-amber-400"></i>
            عنوان التوصيل
          </p>
          <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Cairo', sans-serif" }}>{data.city}</p>
          <p className="text-gray-400 text-xs mt-1 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>{data.address}</p>
          {data.notes && (
            <p className="text-gray-500 text-xs mt-2 italic" style={{ fontFamily: "'Cairo', sans-serif" }}>
              ملاحظة: {data.notes}
            </p>
          )}
        </div>

        {/* Items */}
        <div className="px-5 py-4 border-b border-white/5">
          <p className="text-gray-500 text-xs mb-3 flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
            <i className="ri-shopping-bag-3-line text-amber-400"></i>
            المنتجات ({data.items.length})
          </p>
          <div className="flex flex-col gap-2">
            {data.items.map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <img src={item.image} alt={item.nameAr} className="w-8 h-8 rounded-lg object-cover object-top flex-shrink-0" />
                  <span className="text-gray-300 text-xs truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    {item.nameAr}
                  </span>
                  <span className="text-gray-600 text-xs flex-shrink-0">×{item.qty}</span>
                </div>
                <span className="text-amber-400 text-xs font-bold flex-shrink-0" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {(item.price * item.qty).toLocaleString()} ر.س
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment + Discount + Total */}
        <div className="px-5 py-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-secure-payment-line text-amber-400 text-sm"></i>
              <span className="text-gray-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>{data.paymentLabel}</span>
            </div>
          </div>

          {data.discountCode && (
            <div className="flex items-center justify-between bg-green-400/8 rounded-xl px-3 py-2">
              <div className="flex items-center gap-1.5">
                <i className="ri-coupon-3-line text-green-400 text-xs"></i>
                <span className="text-green-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {data.discountCode} ({data.discountPercent}% خصم)
                </span>
              </div>
              <span className="text-green-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                — {data.discountAmount.toLocaleString()} ر.س
              </span>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-white/5 pt-3">
            <span className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>الإجمالي النهائي</span>
            <div className="text-left">
              {data.discountCode && (
                <p className="text-gray-500 text-xs line-through text-left" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {data.totalPrice.toLocaleString()} ر.س
                </p>
              )}
              <span className="text-amber-400 font-black text-xl" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {data.finalPrice.toLocaleString()} ر.س
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Status steps */}
      <div className="flex flex-col sm:flex-row gap-3 mb-8 w-full max-w-md">
        {[
          { icon: "ri-checkbox-circle-line", label: "تم استلام الطلب", color: "text-green-400", active: true },
          { icon: "ri-truck-line", label: "جاري التجهيز", color: "text-amber-400", active: false },
          { icon: "ri-home-4-line", label: "التوصيل", color: "text-gray-600", active: false },
        ].map((step) => (
          <div
            key={step.label}
            className={`flex-1 flex flex-col items-center gap-2 border rounded-2xl py-4 px-3 ${step.active ? "bg-green-400/5 border-green-400/20" : "bg-[#111] border-white/5"}`}
          >
            <i className={`${step.icon} text-2xl ${step.color}`}></i>
            <p className={`text-xs font-medium ${step.active ? "text-white" : "text-gray-500"}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
              {step.label}
            </p>
          </div>
        ))}
      </div>

      {/* Rate your products nudge */}
      <div className="w-full max-w-md mb-8">
        <div className="bg-[#111] border border-amber-400/20 rounded-2xl p-5 text-right">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-400/15 rounded-xl flex-shrink-0">
              <i className="ri-star-smile-line text-amber-400 text-xl"></i>
            </div>
            <div>
              <p className="text-white font-black text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                قيّم منتجاتك بعد الاستلام
              </p>
              <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                تقييمك يساعد العملاء الآخرين على الاختيار الصحيح
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {data.items.map((item) => (
              <Link
                key={item.id}
                to={`/product/${item.id}`}
                className="flex items-center gap-3 bg-[#1a1a1a] hover:bg-[#1f1f1f] border border-white/5 hover:border-amber-400/20 rounded-xl px-3 py-2.5 transition-all group cursor-pointer"
              >
                <img src={item.image} alt={item.nameAr} className="w-10 h-10 rounded-lg object-cover object-top flex-shrink-0" />
                <span className="flex-1 text-gray-300 text-xs text-right truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {item.nameAr}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {[1,2,3,4,5].map(s => (
                    <i key={s} className="ri-star-line text-amber-400/40 group-hover:text-amber-400/70 text-xs transition-colors"></i>
                  ))}
                </div>
                <i className="ri-arrow-left-s-line text-gray-600 group-hover:text-amber-400 text-sm transition-colors flex-shrink-0"></i>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
        {autoSent ? (
          /* Auto-sent: show a "resend manually" secondary button */
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex-1 flex items-center justify-center gap-2 bg-[#111] border border-green-500/30 hover:border-green-500/60 text-green-400 font-bold py-4 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-whatsapp-line text-lg"></i>
            إعادة الإرسال يدوياً
          </a>
        ) : (
          /* Manual fallback: prominent resend button */
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-400 text-white font-black py-4 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-send-plane-line text-lg"></i>
            إرسال الطلب عبر واتساب
          </a>
        )}

        <Link
          to="/"
          className="flex-1 flex items-center justify-center gap-2 bg-[#111] border border-white/10 hover:border-amber-400/40 text-white font-bold py-4 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
          style={{ fontFamily: "'Cairo', sans-serif" }}
        >
          <i className="ri-store-line text-amber-400"></i>
          العودة للمتجر
        </Link>
      </div>

      <p className="text-gray-600 text-xs mt-5 max-w-xs leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
        {autoSent
          ? "تم إرسال إشعار تلقائي لفريق المبيعات — يمكنك إعادة الإرسال يدوياً إذا لزم"
          : "اضغط الزر أعلاه لإرسال تفاصيل طلبك لفريق المبيعات عبر واتساب"}
      </p>
    </div>
  );
}
